import { memo } from "react";
import { AssignDeviceBoxes } from "./AssignDeviceBoxes";
import { BatchedRects } from "./BatchedRects";
import { CablePathsLayer } from "./CablePathsLayer";
import { CableTrayLayer } from "./CableTrayLayer";
import { CableTrayHighlight } from "./CableTrayHighlight";
import { CircuitPathsLayer } from "./CircuitPathsLayer";
import { RoutesCreationLayer } from "./RoutesCreationLayer";
import { PolygonsLayer } from "./PolygonsLayer";
import { FloorPlanLayoutLayer } from "./FloorPlanLayoutLayer";
import { StickyNotes } from "./StickyNotes";
import { ZOOM_CONFIG } from "../constants";
import type { SvgOverlayProps } from "./types";
import type { CircuitPathsState } from "../../hooks/useCircuitCablePaths/types";

const NO_CIRCUIT_PATHS: CircuitPathsState = Object.freeze({
  paths: [],
  distributionBoardPosition: null,
}) as CircuitPathsState;

export const SvgOverlay = memo(function SvgOverlay({
  objects,
  backgroundObjects,
  selectedObject,
  zoomLevel,
  panOffset,
  floorPlanCablePaths,
  circuitPaths,
  nodeHandlesForRouteId,
  onSelectedBboxChange,
  routeCreationOverlay,
  polygonsOverlay,
  floorPlanLayoutOverlay,
  shouldAnimateTransition = false,
  circleSizeScale,
  opacityThreshold,
  maxZoom,
  circuitStrokeScale,
  cableTrays,
  highlightTrayType,
  focusedTrayType,
  circuitBoardMarkers,
  cableGhostMarker,
  removedMarkers,
  unroutedDeviceMarkers,
  candidateDeviceMarkers,
  selectedDeviceMarkers,
  selectedDeviceMarkersBlink,
  areRouteLinesHidden,
  reshapeView,
  highlightMarker,
  heightMarks,
  zoneDraft,
  feedAreas,
  planWalls,
  wallDraft,
  wallSelection,
  assignDeviceBoxes,
  onSelectNote,
  areNotesHidden = false,
}: SvgOverlayProps) {
  const hasCircuitPaths = circuitPaths?.paths && circuitPaths.paths.length > 0;
  const showBackgroundObjects = !hasCircuitPaths && !!backgroundObjects?.length;
  // Karl's board step draws the boards and their zones with no cable at all.
  const isCableLayerShown =
    hasCircuitPaths || !!circuitBoardMarkers?.length || !!feedAreas?.length;

  return (
    <>
      <svg
        width="100%"
        height="100%"
        className="pointer-events-none"
        overflow="hidden"
      >
        <g
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
            transition: shouldAnimateTransition
              ? ZOOM_CONFIG.PAN_TRANSITION
              : "none",
          }}
        >
          {showBackgroundObjects && (
            <BatchedRects
              objects={backgroundObjects}
              selectedObject={null}
              zoomLevel={zoomLevel}
              opacityThreshold={opacityThreshold}
              maxZoom={maxZoom}
            />
          )}
          {!hasCircuitPaths && (
            <BatchedRects
              objects={objects}
              selectedObject={selectedObject}
              zoomLevel={zoomLevel}
              opacityThreshold={opacityThreshold}
              maxZoom={maxZoom}
              onSelectedBboxChange={onSelectedBboxChange}
            />
          )}
          {/* With cable on the plan the rooms are the ground it runs over, so they lie under all of it. */}
          {floorPlanLayoutOverlay && isCableLayerShown && (
            <FloorPlanLayoutLayer
              overlay={floorPlanLayoutOverlay}
              zoomLevel={zoomLevel}
            />
          )}
          {floorPlanCablePaths && (
            <CablePathsLayer cablePaths={floorPlanCablePaths} />
          )}
          {/* Under the cables, because a cable in a tray is drawn on top of it. */}
          {cableTrays && (
            <CableTrayLayer
              trays={cableTrays}
              strokeScale={circuitStrokeScale}
              highlightType={focusedTrayType ?? highlightTrayType}
            />
          )}
          {isCableLayerShown && (
            <CircuitPathsLayer
              nodeHandlesForRouteId={nodeHandlesForRouteId}
              circuitPaths={circuitPaths ?? NO_CIRCUIT_PATHS}
              selectedObject={selectedObject}
              zoomLevel={zoomLevel}
              circleSizeScale={circleSizeScale}
              opacityThreshold={opacityThreshold}
              isHoverEnabled={!routeCreationOverlay}
              strokeScale={circuitStrokeScale}
              boardMarkers={circuitBoardMarkers}
              ghostMarker={cableGhostMarker}
              removedMarkers={removedMarkers}
              unroutedDeviceMarkers={unroutedDeviceMarkers}
              candidateDeviceMarkers={candidateDeviceMarkers}
              selectedDeviceMarkers={selectedDeviceMarkers}
              selectedDeviceMarkersBlink={selectedDeviceMarkersBlink}
              areRouteLinesHidden={areRouteLinesHidden}
              reshapeView={reshapeView}
              highlightMarker={highlightMarker}
              heightMarks={heightMarks}
              zoneDraft={zoneDraft}
              feedAreas={feedAreas}
              planWalls={planWalls}
              wallDraft={wallDraft}
              wallSelection={wallSelection}
            />
          )}
          {assignDeviceBoxes && <AssignDeviceBoxes boxes={assignDeviceBoxes} />}
          {cableTrays && (
            <CableTrayHighlight
              trays={cableTrays}
              trayType={focusedTrayType ?? null}
              strokeScale={circuitStrokeScale}
            />
          )}
          {floorPlanLayoutOverlay && !isCableLayerShown && (
            <FloorPlanLayoutLayer
              overlay={floorPlanLayoutOverlay}
              zoomLevel={zoomLevel}
            />
          )}
          {polygonsOverlay && (
            <PolygonsLayer
              overlay={polygonsOverlay}
              zoomLevel={zoomLevel}
              circleSizeScale={circleSizeScale}
            />
          )}
          {routeCreationOverlay && (
            <RoutesCreationLayer
              overlay={routeCreationOverlay}
              zoomLevel={zoomLevel}
              circleSizeScale={circleSizeScale}
            />
          )}
        </g>
      </svg>
      {/* HTML above the SVG so note text renders at real pixel sizes, not scaled with the plan. */}
      {!hasCircuitPaths && !areNotesHidden && (
        <StickyNotes
          objects={objects}
          zoomLevel={zoomLevel}
          panOffset={panOffset}
          shouldAnimateTransition={shouldAnimateTransition}
          onSelectNote={onSelectNote}
          hiddenNoteObjectId={
            onSelectedBboxChange === undefined
              ? null
              : (selectedObject?.id ?? null)
          }
        />
      )}
    </>
  );
});
